import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type AccountLocale = 'ru' | 'en';

const STORAGE_KEY = 'rconf-locale';
const cyrillicPattern = /[А-Яа-яЁё]/;
const placeholderPattern = /\$\{[^}]+\}|\{\{[^}]+\}\}|\{[a-zA-Z_][\w.]*\}/g;
const translatableAttributes = ['placeholder', 'title', 'aria-label', 'data-bs-original-title', 'data-title', 'alt'];
let englishCatalog: Record<string, string> = {};
let catalogPromise: Promise<void> | null = null;
const originalText = new WeakMap<Text, string>();
const originalAttributes = new WeakMap<Element, Map<string, string>>();

interface LocaleContextValue {
  locale: AccountLocale;
  setLocale(locale: AccountLocale): void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function normalize(value: unknown): string {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function normalizeLocale(value: unknown): AccountLocale {
  return String(value || '').toLowerCase().startsWith('en') ? 'en' : 'ru';
}

function initialLocale(): AccountLocale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) return normalizeLocale(stored);
  } catch {
    // The browser language remains a safe fallback when storage is unavailable.
  }
  return normalizeLocale(window.navigator.language);
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

interface TemplateEntry {
  pattern: RegExp;
  placeholders: string[];
  translated: string;
}

function templateEntry(source: string, translated: string): TemplateEntry | null {
  const sourcePlaceholders = [...source.matchAll(placeholderPattern)];
  if (!sourcePlaceholders.length) return null;

  const staticText = source.replace(placeholderPattern, '');
  if (!cyrillicPattern.test(staticText) || staticText.replace(/[^\p{L}\p{N}]/gu, '').length < 2) return null;

  let cursor = 0;
  let pattern = '^';
  for (const placeholder of sourcePlaceholders) {
    pattern += escapeRegex(source.slice(cursor, placeholder.index));
    pattern += '(.+?)';
    cursor = Number(placeholder.index) + placeholder[0].length;
  }
  pattern += `${escapeRegex(source.slice(cursor))}$`;

  return {
    pattern: new RegExp(pattern, 'u'),
    placeholders: sourcePlaceholders.map(([placeholder]) => placeholder),
    translated,
  };
}

let templateCatalog: TemplateEntry[] = [];
const russianDateWords: Record<string, string> = {
  январь: 'January', января: 'January', янв: 'Jan',
  февраль: 'February', февраля: 'February', фев: 'Feb',
  март: 'March', марта: 'March', мар: 'Mar',
  апрель: 'April', апреля: 'April', апр: 'Apr',
  май: 'May', мая: 'May',
  июнь: 'June', июня: 'June', июн: 'Jun',
  июль: 'July', июля: 'July', июл: 'Jul',
  август: 'August', августа: 'August', авг: 'Aug',
  сентябрь: 'September', сентября: 'September', сент: 'Sep',
  октябрь: 'October', октября: 'October', окт: 'Oct',
  ноябрь: 'November', ноября: 'November', нояб: 'Nov',
  декабрь: 'December', декабря: 'December', дек: 'Dec',
  понедельник: 'Monday', пн: 'Mon',
  вторник: 'Tuesday', вт: 'Tue',
  среда: 'Wednesday', ср: 'Wed',
  четверг: 'Thursday', чт: 'Thu',
  пятница: 'Friday', пт: 'Fri',
  суббота: 'Saturday', сб: 'Sat',
  воскресенье: 'Sunday', вс: 'Sun',
};
const russianDateWordPattern = new RegExp(`\\b(${Object.keys(russianDateWords).join('|')})\\.?\\b`, 'giu');

function ensureEnglishCatalog(): Promise<void> {
  if (Object.keys(englishCatalog).length) return Promise.resolve();
  if (!catalogPromise) {
    catalogPromise = Promise.all([
      import('./locales/legacy-en.generated.js'),
      import('./locales/legacy-en-overrides.js'),
    ]).then(([generated, overrides]) => {
      englishCatalog = { ...generated.default, ...overrides.default };
      templateCatalog = Object.entries(englishCatalog)
        .map(([source, translated]) => templateEntry(source, translated))
        .filter((entry): entry is TemplateEntry => Boolean(entry))
        .sort((left, right) => right.pattern.source.length - left.pattern.source.length);
    });
  }
  return catalogPromise;
}

function applyTemplate(entry: TemplateEntry, match: RegExpMatchArray): string {
  const captured = entry.placeholders.map((placeholder, index) => ({
    placeholder,
    value: match[index + 1],
  }));
  return entry.translated.replace(placeholderPattern, (placeholder) => {
    const capture = captured.find((item) => item.placeholder === placeholder);
    return capture ? translateLegacyText(capture.value, 'en') : placeholder;
  });
}

function translateLegacyText(value: unknown, targetLocale: AccountLocale): string {
  const source = normalize(value);
  if (!source || targetLocale !== 'en') return source;

  const exact = englishCatalog[source];
  if (exact) return exact;

  for (const entry of templateCatalog) {
    const match = source.match(entry.pattern);
    if (match) return applyTemplate(entry, match);
  }

  for (const pattern of [
    /^(.+?)\s*·\s*(\d+)$/u,
    /^(.+?)\s*\((\d+)\)$/u,
    /^(\d+)\s+(.+)$/u,
  ]) {
    const match = source.match(pattern);
    if (!match) continue;
    const left = translateLegacyText(match[1], 'en');
    const right = translateLegacyText(match[2], 'en');
    if (left === match[1] && right === match[2]) continue;
    if (pattern.source.includes('·')) return `${left} · ${right}`;
    if (pattern.source.includes('\\(')) return `${left} (${right})`;
    return `${left} ${right}`;
  }

  const dateLocalized = source.replace(russianDateWordPattern, (word) => {
    const normalizedWord = word.replace('.', '').toLowerCase();
    return russianDateWords[normalizedWord] || word;
  });
  if (dateLocalized !== source && (/\d/.test(source) || source.split(/\s+/).length <= 4)) return dateLocalized;
  return source;
}

function localizedValue(original: string, locale: AccountLocale): string {
  const prefix = original.match(/^\s*/)?.[0] || '';
  const suffix = original.match(/\s*$/)?.[0] || '';
  return `${prefix}${translateLegacyText(original, locale)}${suffix}`;
}

function shouldSkip(node: Node): boolean {
  const element = node instanceof Element ? node : node.parentElement;
  return !element || Boolean(element.closest('script, style, [contenteditable="true"], [data-i18n-skip]'));
}

function localizeTextNode(node: Text, locale: AccountLocale): void {
  if (shouldSkip(node)) return;
  const current = node.nodeValue || '';
  let original = originalText.get(node);

  if (original === undefined) {
    if (!cyrillicPattern.test(current)) return;
    original = current;
    originalText.set(node, original);
  } else {
    const english = localizedValue(original, 'en');
    if (current !== original && current !== english) {
      if (!cyrillicPattern.test(current)) return;
      original = current;
      originalText.set(node, original);
    }
  }

  const next = localizedValue(original, locale);
  if (current !== next) node.nodeValue = next;
}

function attributesFor(element: Element): Map<string, string> {
  let originals = originalAttributes.get(element);
  if (!originals) {
    originals = new Map();
    originalAttributes.set(element, originals);
  }
  return originals;
}

function localizeAttribute(element: Element, attribute: string, locale: AccountLocale): void {
  if (!element.hasAttribute(attribute) || shouldSkip(element)) return;
  const current = element.getAttribute(attribute) || '';
  const originals = attributesFor(element);
  let original = originals.get(attribute);

  if (original === undefined) {
    if (!cyrillicPattern.test(current)) return;
    original = current;
    originals.set(attribute, original);
  } else {
    const english = translateLegacyText(original, 'en');
    if (current !== original && current !== english) {
      if (!cyrillicPattern.test(current)) return;
      original = current;
      originals.set(attribute, original);
    }
  }

  const next = translateLegacyText(original, locale);
  if (current !== next) element.setAttribute(attribute, next);
}

function localizeElement(element: Element, locale: AccountLocale): void {
  for (const attribute of translatableAttributes) localizeAttribute(element, attribute, locale);
  if (element instanceof HTMLInputElement && ['button', 'reset', 'submit'].includes(element.type)) {
    localizeAttribute(element, 'value', locale);
  }
}

function localizeTree(root: Node, locale: AccountLocale): void {
  if (shouldSkip(root)) return;
  if (root instanceof Text) {
    localizeTextNode(root, locale);
    return;
  }
  if (!(root instanceof Element)) return;

  localizeElement(root, locale);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    if (node instanceof Text) localizeTextNode(node, locale);
    else if (node instanceof Element) localizeElement(node, locale);
    node = walker.nextNode();
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<AccountLocale>(initialLocale);
  const setLocale = useCallback((nextLocale: AccountLocale) => {
    setLocaleState(normalizeLocale(nextLocale));
    try {
      window.localStorage.setItem(STORAGE_KEY, normalizeLocale(nextLocale));
    } catch {
      // Switching still works for the current page when storage is unavailable.
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = 'ltr';
    const root = document.getElementById('root');
    if (!root) return undefined;
    let active = true;
    let observer: MutationObserver | null = null;

    void (async () => {
      if (locale === 'en') await ensureEnglishCatalog();
      if (!active) return;
      localizeTree(root, locale);
      observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes') {
            localizeAttribute(mutation.target as Element, mutation.attributeName || '', locale);
          } else if (mutation.type === 'characterData') {
            localizeTextNode(mutation.target as Text, locale);
          } else {
            mutation.addedNodes.forEach((node) => localizeTree(node, locale));
          }
        }
      });
      observer.observe(root, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: [...translatableAttributes, 'value'],
      });
    })();
    return () => {
      active = false;
      observer?.disconnect();
    };
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useAccountLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useAccountLocale must be used inside I18nProvider');
  return value;
}

export function LocaleSwitcher({ className = '' }: { className?: string }) {
  const { locale, setLocale } = useAccountLocale();
  const languageLabel = locale === 'en' ? 'Interface language' : 'Язык интерфейса';
  return <div className={`locale-switcher ${className}`.trim()} role="group" aria-label={languageLabel}>
    <span className="locale-switcher-label">{languageLabel}</span>
    <span className="locale-switcher-options">
      {(['ru', 'en'] as const).map((option) => <button
        key={option}
        type="button"
        className={`locale-switcher-option${locale === option ? ' active' : ''}`}
        aria-pressed={locale === option}
        title={option === 'ru' ? 'Русский' : 'English'}
        onClick={() => setLocale(option)}
      >{option.toUpperCase()}</button>)}
    </span>
  </div>;
}
